import React from 'react';
import styled, { css, keyframes } from 'styled-components';

type SkeletonVariant = 'text' | 'circular' | 'rectangular';
type SkeletonAnimation = 'shimmer' | 'pulse' | 'none';

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const pulse = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
  100% {
    opacity: 1;
  }
`;

const getVariantStyles = (variant: SkeletonVariant) => {
  switch (variant) {
    case 'circular':
      return css`
        border-radius: 50%;
      `;
    case 'text':
      return css`
        border-radius: 4px;
        transform: scale(1, 0.6);
      `;
    case 'rectangular':
    default:
      return css`
        border-radius: 4px;
      `;
  }
};

const getAnimationStyles = (animation: SkeletonAnimation) => {
  switch (animation) {
    case 'shimmer':
      return css`
        background: linear-gradient(
          90deg,
          ${({ theme }) => theme.colors.gray10} 0%,
          ${({ theme }) => theme.colors.gray20} 50%,
          ${({ theme }) => theme.colors.gray10} 100%
        );
        background-size: 200% 100%;
        animation: ${shimmer} 1.5s ease-in-out infinite;
      `;
    case 'pulse':
      return css`
        background-color: ${({ theme }) => theme.colors.gray20};
        animation: ${pulse} 1.5s ease-in-out infinite;
      `;
    case 'none':
    default:
      return css`
        background-color: ${({ theme }) => theme.colors.gray20};
      `;
  }
};

interface StyledSkeletonProps {
  $width?: string | number;
  $height?: string | number;
  $variant: SkeletonVariant;
  $animation: SkeletonAnimation;
  $borderRadius?: string | number;
}

const StyledSkeleton = styled.div<StyledSkeletonProps>`
  display: block;
  width: ${({ $width }) => (typeof $width === 'number' ? `${$width}px` : $width || '100%')};
  height: ${({ $height }) => (typeof $height === 'number' ? `${$height}px` : $height || '20px')};
  ${({ $variant }) => getVariantStyles($variant)}
  ${({ $animation }) => getAnimationStyles($animation)}
  ${({ $borderRadius }) =>
    $borderRadius !== undefined &&
    css`
      border-radius: ${typeof $borderRadius === 'number' ? `${$borderRadius}px` : $borderRadius};
    `}
`;

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Width of the skeleton */
  width?: string | number;
  /** Height of the skeleton */
  height?: string | number;
  /** Variant of the skeleton shape */
  variant?: SkeletonVariant;
  /** Animation type */
  animation?: SkeletonAnimation;
  /** Custom border radius (overrides variant default) */
  borderRadius?: string | number;
}

const Skeleton: React.FC<SkeletonProps> = ({
  width,
  height,
  variant = 'rectangular',
  animation = 'shimmer',
  borderRadius,
  className,
  style,
  ...rest
}) => {
  return (
    <StyledSkeleton
      $width={width}
      $height={height}
      $variant={variant}
      $animation={animation}
      $borderRadius={borderRadius}
      className={className}
      style={style}
      {...rest}
    />
  );
};

export default Skeleton;
